import React, { useState, useMemo } from 'react';
import CalcShell, { StepsPanel } from '../CalcShell';
import { fmt, PieChart } from './_finHelpers';

export default function ROICalc() {
  const [invested, setInvested] = useState(1000);
  const [returned, setReturned] = useState(2000);
  const [mode, setMode] = useState('dates');
  const [from, setFrom] = useState('2026-07-22');
  const [to, setTo] = useState('2030-12-31');
  const [years, setYears] = useState(4.444);
  const c = useMemo(() => {
    const gain = returned - invested;
    const roi = invested > 0 ? (gain/invested)*100 : 0;
    let len;
    if (mode === 'dates') {
      const d1 = new Date(from), d2 = new Date(to);
      len = (d2 - d1) / (1000*60*60*24*365.25);
    } else len = years;
    const annualized = len > 0 ? (Math.pow(returned/invested, 1/len) - 1) * 100 : 0;
    return { gain, roi, annualized, len };
  }, [invested, returned, mode, from, to, years]);
  return (
    <CalcShell look="cnet" title="Return on Investment (ROI) Calculator" subtitle="Compute investment gain, total ROI %, and annualized ROI over a time period."
      about={<><p><b>ROI</b> measures the profitability of an investment as a percentage. Annualized ROI normalizes returns to a per-year basis for comparison across investments of different durations.</p></>}
    >
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        <div>
          <div className="field"><label>Amount Invested</label><div className="flex gap-2"><span className="self-center text-slate-500">$</span><input type="number" value={invested} onChange={e=>setInvested(+e.target.value||0)} className="flex-1"/></div></div>
          <div className="field"><label>Amount Returned</label><div className="flex gap-2"><span className="self-center text-slate-500">$</span><input type="number" value={returned} onChange={e=>setReturned(+e.target.value||0)} className="flex-1"/></div></div>
          <div className="field"><label>Investment Time</label>
            <div className="flex gap-4 mt-1 text-sm"><label><input type="radio" checked={mode==='dates'} onChange={()=>setMode('dates')}/> Use Dates</label><label><input type="radio" checked={mode==='length'} onChange={()=>setMode('length')}/> Use Length</label></div>
          </div>
          {mode==='dates' ? (
            <div className="grid grid-cols-2 gap-2 mt-2">
              <div><label className="text-xs">From</label><input type="date" value={from} onChange={e=>setFrom(e.target.value)}/></div>
              <div><label className="text-xs">To</label><input type="date" value={to} onChange={e=>setTo(e.target.value)}/></div>
            </div>
          ) : (
            <div className="field"><label>Length (years)</label><input type="number" step="0.001" value={years} onChange={e=>setYears(+e.target.value||0)}/></div>
          )}
        </div>
        <div>
          <h4 className="font-semibold mb-2">Result</h4>
          <table className="data-table"><tbody>
            <tr><td>Investment Gain</td><td className="text-right font-semibold text-green-700">{fmt(c.gain)}</td></tr>
            <tr><td>ROI</td><td className="text-right font-semibold text-blue-700" data-testid="roi-total">{c.roi.toFixed(2)}%</td></tr>
            <tr><td>Annualized ROI</td><td className="text-right font-semibold">{c.annualized.toFixed(2)}%</td></tr>
            <tr><td>Investment Length</td><td className="text-right">{c.len.toFixed(3)} years</td></tr>
          </tbody></table>
          <div className="mt-4"><PieChart parts={[{label:'Invested', value:invested, color:'#2563eb'},{label:'Profit', value:Math.max(0,c.gain), color:'#10b981'}]} size={170}/></div>
        </div>
      </div>
    </CalcShell>
  );
}
